import { Directive } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';

@Directive()
export abstract class PrPrevoyanceContratBuilder extends PrFormAbstract {
  generate(entity: any): UntypedFormGroup {
    return this.formBuilder.group({
      // Base obligatoire
      providerId: [null, Validators.required], // UI-only: cascade assureur → produit
      productId: [entity?.productId ?? null, Validators.required],
      ownerId: [entity?.ownerId ?? null, Validators.required],
      cotisation: [entity?.cotisation ?? null, Validators.required],
      cotisationPeriode: [entity?.cotisationPeriode ?? null],
      numeroContrat: [entity?.numeroContrat ?? null, Validators.required],
      dateSouscription: [entity?.dateSouscription ?? null, Validators.required],
      commentaire: [entity?.commentaire ?? null],

      // Prévoyance - Régime obligatoire
      enComplementRegimeObligatoire: [entity?.enComplementRegimeObligatoire ?? false],

      // Prévoyance - ITT
      ittMontantMensuel: [entity?.ittMontantMensuel ?? null],
      ittFraisGenerauxMensuels: [entity?.ittFraisGenerauxMensuels ?? null],
      ittFranchises: [entity?.ittFranchises ?? null],
      ittRenfortDos: [entity?.ittRenfortDos ?? false],
      ittAffectionPsy: [entity?.ittAffectionPsy ?? false],
      ittDelaisAttente: [entity?.ittDelaisAttente ?? null],
      ittMiTempsTherapeutique: [entity?.ittMiTempsTherapeutique ?? false],

      // Prévoyance - Invalidité
      invaliditeMontantMensuel: [entity?.invaliditeMontantMensuel ?? null],
      invaliditeBareme: [entity?.invaliditeBareme ?? null],
      invaliditeSeuilDeclenchement: [entity?.invaliditeSeuilDeclenchement ?? null],

      // Prévoyance - Décès/PTIA
      decesPtiaMontant: [entity?.decesPtiaMontant ?? null],

      // Prévoyance - Cotisations
      cotisationMensuelle: [entity?.cotisationMensuelle ?? null],
      cotisationMadelin: [entity?.cotisationMadelin ?? null],

      // AE - Quotités
      quotiteDeces: [entity?.quotiteDeces ?? null],
      quotitePtia: [entity?.quotitePtia ?? null],
      quotiteIpt: [entity?.quotiteIpt ?? null],
      quotiteItt: [entity?.quotiteItt ?? null],
      quotiteIpp: [entity?.quotiteIpp ?? null],
      quotitePe: [entity?.quotitePe ?? null],

      // AE - Franchises
      franchiseIttJours: [entity?.franchiseIttJours ?? null],
      franchisePeJours: [entity?.franchisePeJours ?? null],
      typeIndemnisation: [entity?.typeIndemnisation ?? null],
      priseEnChargeDosPsy: [entity?.priseEnChargeDosPsy ?? false],

      // Mutuelle - Hospitalisation
      hospitalisationHonorairesOptam: [entity?.hospitalisationHonorairesOptam ?? null],
      hospitalisationHonorairesNonOptam: [entity?.hospitalisationHonorairesNonOptam ?? null],
      hospitalisationChambreParticuliere: [entity?.hospitalisationChambreParticuliere ?? null],
      hospitalisationPrimeMaternite: [entity?.hospitalisationPrimeMaternite ?? null],

      // Mutuelle - Optique
      optiqueLunettes: [entity?.optiqueLunettes ?? null],
      optiqueLentilles: [entity?.optiqueLentilles ?? null],
      optiquePlafond: [entity?.optiquePlafond ?? null],
      optiqueChirurgieLaser: [entity?.optiqueChirurgieLaser ?? null],

      // Mutuelle - Dentaire
      dentaireProthese: [entity?.dentaireProthese ?? null],
      dentaireOrthodontieAdulte: [entity?.dentaireOrthodontieAdulte ?? null],
      dentaireOrthodontieEnfant: [entity?.dentaireOrthodontieEnfant ?? null],

      // Mutuelle - Pharmacie / Médecines douces / Consultations / Cotisations
      pharmaciePriseEnCharge: [entity?.pharmaciePriseEnCharge ?? null],
      medecinesDouces: [entity?.medecinesDouces ?? null],
      consultationsOptamCas: [entity?.consultationsOptamCas ?? null],
      consultationsNonOptamNonCas: [entity?.consultationsNonOptamNonCas ?? null],
      cotisationsMontant: [entity?.cotisationsMontant ?? null],
    });
  }

  extract(): any {
    const v = this.form.value;
    return {
      productId: v.productId ? (typeof v.productId === 'object' ? v.productId.id : parseInt(v.productId)) : null,
      ownerId: v.ownerId ? (typeof v.ownerId === 'object' ? v.ownerId.id : parseInt(v.ownerId)) : null,
      cotisation: this.parseFloat(v.cotisation),
      cotisationPeriode: this.emptyIsNull(v.cotisationPeriode),
      numeroContrat: this.emptyIsNull(v.numeroContrat),
      dateSouscription: v.dateSouscription
        ? new Date(v.dateSouscription).toISOString().split('T')[0]
        : null,
      dateDebut: v.dateSouscription
        ? new Date(v.dateSouscription).toISOString().split('T')[0]
        : null,
      commentaire: this.emptyIsNull(v.commentaire),
      enComplementRegimeObligatoire: v.enComplementRegimeObligatoire || false,
      ittMontantMensuel: this.parseFloat(v.ittMontantMensuel),
      ittFraisGenerauxMensuels: this.parseFloat(v.ittFraisGenerauxMensuels),
      ittFranchises: this.emptyIsNull(v.ittFranchises),
      ittRenfortDos: v.ittRenfortDos || false,
      ittAffectionPsy: v.ittAffectionPsy || false,
      ittDelaisAttente: this.emptyIsNull(v.ittDelaisAttente),
      ittMiTempsTherapeutique: v.ittMiTempsTherapeutique || false,
      invaliditeMontantMensuel: this.parseFloat(v.invaliditeMontantMensuel),
      invaliditeBareme: this.emptyIsNull(v.invaliditeBareme),
      invaliditeSeuilDeclenchement: this.parseFloat(v.invaliditeSeuilDeclenchement),
      decesPtiaMontant: this.parseFloat(v.decesPtiaMontant),
      cotisationMensuelle: this.emptyIsNull(v.cotisationMensuelle),
      cotisationMadelin: this.emptyIsNull(v.cotisationMadelin),
      quotiteDeces: this.parseFloat(v.quotiteDeces),
      quotitePtia: this.parseFloat(v.quotitePtia),
      quotiteIpt: this.parseFloat(v.quotiteIpt),
      quotiteItt: this.parseFloat(v.quotiteItt),
      quotiteIpp: this.parseFloat(v.quotiteIpp),
      quotitePe: this.parseFloat(v.quotitePe),
      franchiseIttJours: v.franchiseIttJours ? parseInt(v.franchiseIttJours) : null,
      franchisePeJours: v.franchisePeJours ? parseInt(v.franchisePeJours) : null,
      typeIndemnisation: this.emptyIsNull(v.typeIndemnisation),
      priseEnChargeDosPsy: v.priseEnChargeDosPsy || false,
      // Mutuelle
      hospitalisationHonorairesOptam: this.parseFloat(v.hospitalisationHonorairesOptam),
      hospitalisationHonorairesNonOptam: this.parseFloat(v.hospitalisationHonorairesNonOptam),
      hospitalisationChambreParticuliere: this.parseFloat(v.hospitalisationChambreParticuliere),
      hospitalisationPrimeMaternite: this.emptyIsNull(v.hospitalisationPrimeMaternite),
      optiqueLunettes: this.parseFloat(v.optiqueLunettes),
      optiqueLentilles: this.parseFloat(v.optiqueLentilles),
      optiquePlafond: this.parseFloat(v.optiquePlafond),
      optiqueChirurgieLaser: this.parseFloat(v.optiqueChirurgieLaser),
      dentaireProthese: this.parseFloat(v.dentaireProthese),
      dentaireOrthodontieAdulte: this.emptyIsNull(v.dentaireOrthodontieAdulte),
      dentaireOrthodontieEnfant: this.emptyIsNull(v.dentaireOrthodontieEnfant),
      pharmaciePriseEnCharge: this.parseFloat(v.pharmaciePriseEnCharge),
      medecinesDouces: this.emptyIsNull(v.medecinesDouces),
      consultationsOptamCas: this.parseFloat(v.consultationsOptamCas),
      consultationsNonOptamNonCas: this.parseFloat(v.consultationsNonOptamNonCas),
      cotisationsMontant: this.parseFloat(v.cotisationsMontant),
    };
  }
}
